import { Component, DestroyRef, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoggingService, WorksheetService } from '@fgb/core';
import { StreamAMGService } from './steam-amg.service';
import { STREAMAMG_CONFIG } from './stream-amg-config';
import { CommonModule } from '@angular/common';
import { Playable } from 'src/app/shared/directives/playable.directive';

@Component({
  selector: 'fgb-stream-amg-player',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './stream-amg-player.component.html',
  styleUrl: './stream-amg-player.component.scss',
})
export class StreamAMGPlayerComponent extends Playable implements OnInit, OnDestroy {
  @Input() streamKey: string;
  playerId = `stream-player-${crypto.randomUUID()}`;
  protected readonly streamService = inject(StreamAMGService);
  protected readonly loggingService = inject(LoggingService);
  protected readonly streamAmgConfig = inject(STREAMAMG_CONFIG);
  private worksheetService = inject(WorksheetService);
  private playback: any;
  private destroyRef = inject(DestroyRef);
  private playheadInterval: any;
  private isFirstPlay = false;

  ngOnInit() {
    this.streamService
      .loadJs()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.initializePlayer();
        },
        error: () => {},
      });
  }

  ngOnDestroy(): void {
    if (this.playback) {
      this.playback.destroy(this.playerId);
    }

    this.clearPlayheadInterval();
  }

  private initializePlayer() {
    this.playback = (window as any).Playback;

    if (!this.playback) {
      this.loggingService.error('StreamAMG SDK not available');
      return;
    }

    if (!this.streamAmgConfig) {
      this.loggingService.debug('Unable to get config');
      return;
    }

    this.playback.initialize(this.streamAmgConfig.ApiKey);

    const events = {
      PlaybackPlayerReady: (event: any) => {
        console.log('PlaybackPlayerReady Event', event);
        this.onReady.emit();
      },
    };

    this.playback.play({ container: this.playerId, entryId: this.streamKey, options: { autoplay: false }, events });
  }

  public play(): void {
    this.playback.play({
      container: this.playerId,
      entryId: this.streamKey,
      options: {
        muted: false,
        autoplay: true,
      },
      events: {
        PlaybackPlay: (event: any) => {
          if (!this.isFirstPlay) {
            this.isFirstPlay = true;
            this.worksheetService.logViewStreamStart(this.streamKey);
          }
        },
        PlaybackPlaying: (event: any) => {
          if (this.playheadInterval) {
            clearInterval(this.playheadInterval);
          }

          this.playheadInterval = setInterval(() => {
            const timespan = this.playback.getCurrentTime(this.playerId);

            if (Math.floor(timespan) % 20.5 == 19) {
              if (timespan % 20.5 > 19.72) {
                this.worksheetService.logStreamWatching(this.streamKey);
              }
            }
          }, 500);
        },
        PlaybackPaused: (event: any) => {
          this.clearPlayheadInterval();
        },
        PlaybackPlayerFinished: (event: any) => {
          this.clearPlayheadInterval();
        },
      },
    });
  }

  public pause(): void {}

  public stop(): void {}

  private clearPlayheadInterval() {
    if (this.playheadInterval) {
      clearInterval(this.playheadInterval);
      this.playheadInterval = null;
    }
  }

  get style() {
    return {
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      width: '100%',
      height: '100%',
      border: 'none',
    };
  }
}
